import { formatRupeeLabel } from '../../../display-values/lumpsumPlanAmount'
import { formatTargetSipLabel } from '../../../display-values/sipPlanAmount'
import { useIncreaseSipAction } from '../../../execution/sip/increase/usePlanIncreaseSipAction'
import { openRecommendationFundDetail } from '../../../lumpsum/plan-bottom-sheet/open/planRecommendationOpen'
import { PlanRecommendationFundTile } from '../../../plans-ui-components/PlanRecommendationFundTile'
import { SipRecommendationActionType } from '../../api/common.types'
import type { SipIncreaseRecommendation } from '../../api/sipPlan.types'
import type { SipPlanRecommendationDetails } from '../api/sipPlanRecommendation.types'
import { SipPlanRecommendationSheetErrorState } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetErrorState'
import { SipPlanRecommendationSheetLayout } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetLayout'
import { SipPlanRecommendationSheetSkeleton } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetSkeleton'
import { SipIncreasePlanRecommendationBody } from './body/SipIncreasePlanRecommendationBody'
import { PlansFundRoleBySipAction, PlansScreen } from '../../../analytics/plansEvents'
import {
  trackPlansSipFundTileOpen,
  trackPlansSipFundTilePrimaryCta
} from '../../../analytics/plansTrack'
import { ALLOCATION_PLANS_THEME } from '../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'

type SipIncreasePlanRecommendationSheetProps = {
  onDismiss: () => void
  planDetails: SipPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  recommendation: SipIncreaseRecommendation
  initiateSipCta?: unknown
}

export function SipIncreasePlanRecommendationSheet({
  onDismiss,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  recommendation,
  initiateSipCta
}: SipIncreasePlanRecommendationSheetProps) {
  const currentSip = planDetails?.currentSip ?? 0
  const idealSip = planDetails?.idealSip ?? recommendation?.recommendedSipAmount ?? 0
  const currentLabel = `${formatRupeeLabel({ rawAmount: currentSip })}/mo`
  const idealLabel = `${formatTargetSipLabel({ rawAmount: idealSip })}/mo`
  const isin = recommendation?.isin ?? planDetails?.isin ?? ''
  const { onIncreaseSip } = useIncreaseSipAction({ isRecommendationSheetSource: true })
  const screenName = PlansScreen.SipYourPlanRecommendation
  const increaseSipActionLabel = `Increase SIP to ${idealLabel}`

  return (
    <SipPlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      analyticsScreen={PlansScreen.SipYourPlanRecommendation}
    >
      {isLoading ? (
        <SipPlanRecommendationSheetSkeleton />
      ) : isFetchFailed ? (
        <SipPlanRecommendationSheetErrorState onRetry={onRetry} analyticsScreen={PlansScreen.SipYourPlanRecommendation} />
      ) : (
        <>
          <div className="px-5">
            <div className="flex flex-col gap-2">
              <div className="text-[14px] font-[500] leading-[130%]" style={{ color: ALLOCATION_PLANS_THEME.headerTextColor }}>
                Allocation Recommendation
              </div>
              <div className="text-[20px] leading-[130%] font-[400] text-[#6A6E6E] max-w-[90%]">
                {'Increase your SIP in this fund from '}
                <span className="font-[500] text-[#232323]">{currentLabel}</span>
                {' to '}
                <span className="font-[500] text-[#232323]">{idealLabel}</span>
              </div>
            </div>
          </div>
          <div className="px-5 pt-9 pb-4">
            <PlanRecommendationFundTile
              mfLogoUrl={planDetails?.mfLogoUrl ?? ''}
              fundName={planDetails?.fundName ?? ''}
              fundNature={planDetails?.fundNature ?? ''}
              fundCategory={planDetails?.fundCategory ?? ''}
              powerRating={planDetails?.powerRating ?? ''}
              powerRank={planDetails?.powerRank ?? null}
              purchaseMode={planDetails?.purchaseMode ?? ''}
              fundIsin={isin}
              onCardClick={() => {
                trackPlansSipFundTileOpen({
                  screenName,
                  fundRole: PlansFundRoleBySipAction[SipRecommendationActionType.INCREASE],
                  fundName: planDetails?.fundName ?? '',
                  fundIsin: isin
                })
                openRecommendationFundDetail(isin)
              }}
              actionFooter={{
                actionLabel: increaseSipActionLabel,
                onActionClick: () => {
                  trackPlansSipFundTilePrimaryCta({
                    screenName,
                    buttonLabel: increaseSipActionLabel,
                    fundName: planDetails?.fundName ?? '',
                    fundIsin: isin
                  })
                  void onIncreaseSip(recommendation, initiateSipCta)
                }
              }}
            />
          </div>
          <SipIncreasePlanRecommendationBody planDetails={planDetails} />
        </>
      )}
    </SipPlanRecommendationSheetLayout>
  )
}
